'use client';

import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useAuth } from '@/contexts/auth-context';
import { useGmailAccount } from '@/contexts/gmail-account-context';
import { GmailAccountSelector } from '@/components/gmail-account-selector';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  LogOut,
  Settings,
  List,
  MessageSquare,
  LayoutDashboard,
  Menu,
  X,
  Folder,
  HelpCircle,
} from 'lucide-react';
import { useHelp } from '@/contexts/help-context';
import { api } from '@/lib/api';
import { logError } from '@/lib/utils';
import { useEffect, useCallback } from 'react';
import { useMobileMenu } from '@/hooks/use-mobile-menu';
import { isUserPaid, isTrialExpired } from '@/lib/utils/subscription';
import { cn } from '@/lib/utils';
import { buildRouteWithAccount } from '@/lib/routes';

const navItems = [
  {
    href: '/dashboard',
    label: 'Dashboard',
    icon: LayoutDashboard,
    description: 'Overview & stats',
  },
  { href: '/messages', label: 'Messages', icon: MessageSquare, description: 'Flagged emails' },
  { href: '/rules', label: 'Rules', icon: List, description: 'Email rules' },
  { href: '/folders', label: 'Folders', icon: Folder, description: 'Email folders' },
  { href: '/settings', label: 'Settings', icon: Settings, description: 'Preferences' },
] as const;

/**
 * Client component for header interactive parts.
 * Handles navigation, user menu, Gmail selector, and logout.
 * The static header structure (logo, container) is pre-rendered by HeaderServer.
 *
 * Features:
 * - Responsive navigation with mobile menu
 * - Gmail account selector (desktop and mobile)
 * - User subscription tier badge
 * - Gmail connection status and OAuth flow
 * - Body scroll lock when mobile menu is open
 * - Accessible keyboard navigation (Escape to close menu)
 * - Auto-closes menu on navigation or user change
 *
 * @note Uses useMobileMenu hook for consistent mobile menu behavior
 * across navigation components.
 */
export function HeaderClient() {
  const { user, loading: authLoading, logout } = useAuth();
  const {
    gmailAccounts,
    selectedGmailAccountId,
    setSelectedGmailAccountId,
    loading: gmailAccountsLoading,
  } = useGmailAccount();
  const pathname = usePathname();
  // Get Gmail connection status and subscription from auth context (no API call needed)
  // Default to false when user is null (conservative security - disable features on auth failure)
  // Also default to false when loading to prevent flicker of enabled features
  const gmailConnected = user?.gmail_connected || false;
  const isPaid = user && !authLoading ? isUserPaid(user) : false; // Default to false for security when user is null or loading
  const restrictedTooltip =
    user && isTrialExpired(user)
      ? 'Your free trial has ended. Please add a payment method to access this feature.'
      : 'Subscription required. Start a free trial to access this feature.';
  const userId = user?.id ?? null;
  const {
    isOpen: mobileMenuOpen,
    toggle: toggleMobileMenu,
    close: closeMobileMenu,
  } = useMobileMenu();
  const { openHelp } = useHelp();

  // Close menu only when the authenticated identity actually changes (login/logout/switch)
  useEffect(() => {
    closeMobileMenu();
  }, [userId, closeMobileMenu]);

  // Handle Escape key to close menu
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && mobileMenuOpen) {
        closeMobileMenu();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [mobileMenuOpen, closeMobileMenu]);

  const handleLogout = useCallback(async () => {
    await logout();
    window.location.href = '/';
  }, [logout]);

  const handleConnectGmail = useCallback(async () => {
    try {
      const response = await api.initiateGoogleOAuth();
      window.location.href = response.authorization_url;
    } catch (error) {
      logError('Failed to initiate Gmail OAuth', { userId: user?.id }, error);
    }
  }, [user?.id]);

  return (
    <>
      {/* Desktop primary navigation lives in the left rail (DashboardSidebar).
          Mobile navigation is handled by the hamburger menu below. */}
      {/* Interactive parts - user menu, Gmail selector, logout */}
      <div className="ml-auto flex items-center gap-2 sm:gap-4">
        {user && (
          <>
            {/* Gmail Account Selector - Desktop */}
            <div className="hidden sm:flex items-center">
              <GmailAccountSelector
                accounts={gmailAccounts}
                selectedId={selectedGmailAccountId}
                onSelect={setSelectedGmailAccountId}
                loading={gmailAccountsLoading}
                size="sm"
              />
            </div>
            <div className="hidden sm:flex items-center">
              <Badge variant="secondary" className="text-xs">
                {user.subscription_tier}
              </Badge>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                className="md:hidden inline-flex items-center justify-center rounded-md border p-2 transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                aria-label="Toggle navigation menu"
                aria-expanded={mobileMenuOpen}
                onClick={toggleMobileMenu}
              >
                {mobileMenuOpen ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
              </button>
              {!gmailConnected && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleConnectGmail}
                  className="text-xs sm:text-sm"
                >
                  <span className="hidden sm:inline">Connect Gmail</span>
                  <span className="sm:hidden">Connect</span>
                </Button>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => openHelp()}
                className="px-2 sm:px-3"
                aria-label="Help and guides"
              >
                <HelpCircle className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">Help</span>
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleLogout}
                className="px-2 sm:px-3"
                aria-label="Logout"
              >
                <LogOut className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">Logout</span>
              </Button>
            </div>
          </>
        )}
      </div>
      {/* Mobile menu */}
      {user && mobileMenuOpen && (
        <>
          <div
            className="md:hidden fixed top-14 left-0 right-0 bottom-0 z-40 bg-black/60 backdrop-blur-sm"
            onClick={closeMobileMenu}
          />
          <div className="md:hidden absolute left-0 right-0 top-full z-50 border-b bg-background/95 shadow-2xl">
            <div className="container px-4 py-4 space-y-4">
              <div className="flex items-center justify-between">
                <Badge variant="secondary" className="w-fit text-xs">
                  {user.subscription_tier}
                </Badge>
              </div>
              {/* Gmail Account Selector - Mobile */}
              <GmailAccountSelector
                accounts={gmailAccounts}
                selectedId={selectedGmailAccountId}
                onSelect={setSelectedGmailAccountId}
                loading={gmailAccountsLoading}
                size="md"
                showLabel={true}
              />
              <nav className="flex flex-col gap-2">
                {navItems.map(({ href, label, icon: Icon, description }) => {
                  const isActive = pathname?.startsWith(href);
                  // Disable Messages, Rules, Folders, Settings for unpaid users (Dashboard always enabled)
                  const isRestricted = href !== '/dashboard' && !isPaid;
                  const hrefWithAccount = buildRouteWithAccount(href, selectedGmailAccountId);

                  if (isRestricted) {
                    return (
                      <div
                        key={href}
                        className="flex items-center gap-3 rounded-lg border bg-muted/30 px-3 py-2 text-sm font-medium text-muted-foreground cursor-not-allowed opacity-50"
                        title={restrictedTooltip}
                      >
                        <Icon className="h-4 w-4" />
                        <div className="flex flex-col">
                          <span>{label}</span>
                          <span className="text-xs text-muted-foreground">{description}</span>
                        </div>
                      </div>
                    );
                  }

                  return (
                    <Link
                      key={href}
                      href={hrefWithAccount}
                      onClick={closeMobileMenu}
                      className={cn(
                        'flex items-center gap-3 rounded-lg border px-3 py-2 text-sm font-medium transition',
                        isActive
                          ? 'border-primary/50 bg-primary/10 text-foreground'
                          : 'border-border bg-muted/50 hover:border-border/80 hover:bg-muted/70'
                      )}
                    >
                      <Icon className="h-4 w-4" />
                      <div className="flex flex-col">
                        <span>{label}</span>
                        <span className="text-xs text-muted-foreground">{description}</span>
                      </div>
                    </Link>
                  );
                })}
              </nav>
              {!gmailConnected && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    closeMobileMenu();
                    handleConnectGmail();
                  }}
                  className="w-full"
                >
                  Connect Gmail
                </Button>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  closeMobileMenu();
                  openHelp();
                }}
                className="w-full"
              >
                <HelpCircle className="h-4 w-4 mr-2" />
                Help &amp; Guides
              </Button>
              <Button variant="destructive" size="sm" onClick={handleLogout} className="w-full">
                Logout
              </Button>
            </div>
          </div>
        </>
      )}
    </>
  );
}
